import React from 'react'
import { Translation } from 'react-i18next'

import { formatDateCell } from 'helpers/review_automation'
import { OrderStatusCell } from '../../components/review_automation/OrdersSection/OrderStatusCell'
import { OrderProductCell } from '../../components/review_automation/OrdersSection/OrderProductCell'
import { UpsellTooltip } from '../../components/review_automation/Upsell/UpsellTooltip'

export const ORDERS_TABLE_COLUMN_NAMES = {
  orderId: (
    <Translation ns="review_automation">
      {t =>
        t(
          'review_automation:ReviewAutomationTable.columns.amazonOrderId',
          'Order ID'
        )
      }
    </Translation>
  ),
  reviewRequestStatus: (
    <Translation ns="review_automation">
      {t =>
        t(
          'review_automation:ReviewAutomationTable.columns.reviewRequestStatus',
          'Review Request Status'
        )
      }
    </Translation>
  ),
  product: (
    <Translation ns="review_automation">
      {t =>
        t('review_automation:ReviewAutomationTable.columns.product', 'Product')
      }
    </Translation>
  ),
  units: (
    <Translation ns="review_automation">
      {t => t('review_automation:ReviewAutomationTable.columns.units', 'Units')}
    </Translation>
  ),
  orderStatus: (
    <Translation ns="review_automation">
      {t =>
        t('review_automation:ReviewAutomationTable.columns.status', 'Status')
      }
    </Translation>
  ),
  orderDate: (
    <Translation ns="review_automation">
      {t =>
        t(
          'review_automation:ReviewAutomationTable.columns.orderDate',
          'Order Date'
        )
      }
    </Translation>
  ),
  estDeliveryDate: (
    <Translation ns="review_automation">
      {t =>
        t(
          'review_automation:ReviewAutomationTable.columns.estDeliveryDate',
          'Est. Delivery Date'
        )
      }
    </Translation>
  )
}

export const getReviewAutomationStatuses = (orderDelay, marketplaceDelay) => {
  return {
    error: {
      statusLabel: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.statuses.error',
              'Amazon Issue'
            )
          }
        </Translation>
      ),
      statusColor: 'red100',
      tooltip: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.tooltip.error',
              "There are sync issues with Amazon's servers. We'll resend the request (3) times. If unsuccessful, we'll skip the order review request"
            )
          }
        </Translation>
      )
    },
    cant_send: {
      statusLabel: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.statuses.cantSend',
              "Can't send request"
            )
          }
        </Translation>
      ),
      statusColor: 'red100',
      tooltip: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.tooltip.cantSend',
              "One or more order items has the send status set to 'Do not send request'"
            )
          }
        </Translation>
      )
    },
    ineligible: {
      statusLabel: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.statuses.ineligible',
              'Ineligible'
            )
          }
        </Translation>
      ),
      statusColor: 'coolGrey300',
      tooltip: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.tooltip.ineligible',
              'This order is ineligible for a review request.'
            )
          }
        </Translation>
      )
    },
    not_sent: {
      statusLabel: (
        <Translation ns="review_automation">
          {t =>
            orderDelay
              ? t(
                  'review_automation:ReviewAutomationTable.scheduled',
                  'Scheduled: {{ delay }} days',
                  {
                    delay: orderDelay
                  }
                )
              : t(
                  'review_automation:ReviewAutomationTable.scheduled_default',
                  'Scheduled: {{ delay }} days (default)',
                  {
                    delay: marketplaceDelay
                  }
                )
          }
        </Translation>
      ),
      statusColor: 'yellow200'
    },
    refunded: {
      statusLabel: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.statuses.refunded',
              'Refunded'
            )
          }
        </Translation>
      ),
      statusColor: 'coolGrey300'
    },
    requested: {
      statusLabel: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.statuses.requested',
              'Requested'
            )
          }
        </Translation>
      ),
      statusColor: 'green200'
    },
    skipped: {
      statusLabel: (
        <Translation ns="review_automation">
          {t =>
            t(
              'review_automation:ReviewAutomationTable.statuses.skippedRequest',
              'Skipped Request'
            )
          }
        </Translation>
      ),
      statusColor: 'red100'
    }
  }
}

export const ORDERS_TABLE_COLUMNS = (updateOrder, showUpsellTooltip) => [
  {
    index: 0,
    position: 0,
    Header: ORDERS_TABLE_COLUMN_NAMES.orderId,
    accessor: 'amazon_order_id',
    defaultSelection: true,
    sortable: false,
    width: 175
  },
  {
    index: 1,
    position: 1,
    Header: ORDERS_TABLE_COLUMN_NAMES.reviewRequestStatus,
    headerTooltip: showUpsellTooltip ? (
      <UpsellTooltip type="order_delay_skip" />
    ) : (
      undefined
    ),
    headerTooltipWithIcon: showUpsellTooltip,
    headerTooltipSize: '280px',
    accessor: 'review_request_status',
    defaultSelection: true,
    sortable: false,
    width: 265,
    // When we modify a Cell, and sub-rows should not render any data,
    // we should explicitly specify to render data only for the parent row here
    Cell: cellData => {
      const {
        row: { depth, original }
      } = cellData
      return depth === 0 ? (
        <OrderStatusCell rowData={original} updateOrder={updateOrder} />
      ) : (
        <span />
      )
    }
  },
  {
    index: 2,
    position: 2,
    Header: ORDERS_TABLE_COLUMN_NAMES.product,
    accessor: 'product',
    defaultSelection: true,
    sortable: false,
    width: 255,
    Cell: cellData => {
      const {
        row: { depth, original, isExpanded }
      } = cellData

      return (
        <OrderProductCell
          rowData={original}
          isSubRow={!!depth}
          isExpanded={isExpanded}
        />
      )
    }
  },
  {
    index: 3,
    position: 3,
    Header: ORDERS_TABLE_COLUMN_NAMES.units,
    accessor: 'quantity',
    defaultSelection: true,
    sortable: true,
    width: 75,
    type: 'number'
  },
  {
    index: 4,
    position: 4,
    Header: ORDERS_TABLE_COLUMN_NAMES.orderStatus,
    accessor: 'order_status',
    defaultSelection: true,
    sortable: true,
    width: 100
  },
  {
    index: 5,
    position: 5,
    Header: ORDERS_TABLE_COLUMN_NAMES.orderDate,
    accessor: 'purchase_date',
    defaultSelection: true,
    sortable: true,
    width: 140,
    Cell: formatDateCell
  },
  {
    index: 6,
    position: 6,
    Header: ORDERS_TABLE_COLUMN_NAMES.estDeliveryDate,
    accessor: 'estimated_arrival_date',
    defaultSelection: true,
    sortable: true,
    width: 160,
    Cell: formatDateCell
  }
]

export const FILTER_OPTIONS = t => [
  {
    name: t('generic:ClearSelection', 'Clear Selection'),
    value: 0,
    clearSelectionId: 1
  },
  {
    name: t('review_automation:ReviewAutomationTable.filters.sent', 'Sent'),
    value: 'sent'
  },
  {
    name: t(
      'review_automation:ReviewAutomationTable.filters.notSent',
      'Not Sent'
    ),
    value: 'not_sent'
  }
]
